import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Hotel, Building, MapPin, Phone, Mail, FileText, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { propertyAPI } from '@/services/api';
import { toast } from 'sonner';

export default function Onboarding() {
  const [formData, setFormData] = useState({
    nombre: '',
    rnt: '',
    direccion: '',
    ciudad: '',
    departamento: '',
    pais: 'Colombia',
    telefono: '',
    email: '',
    tipo: 'Hotel',
    num_habitaciones: '',
    descripcion: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await propertyAPI.create({
        ...formData,
        num_habitaciones: parseInt(formData.num_habitaciones)
      });
      
      toast.success('¡Propiedad registrada exitosamente!');
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al registrar la propiedad');
      toast.error('Error al registrar la propiedad');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <Hotel className="h-12 w-12 text-primary mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-2" style={{fontFamily: 'Manrope'}} data-testid="onboarding-heading">
            Registra tu Propiedad
          </h1>
          <p className="text-muted-foreground">
            Completa la información de tu hotel para comenzar a usar el PMS
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle style={{fontFamily: 'Manrope'}}>Información de la Propiedad</CardTitle>
            <CardDescription>
              Todos los campos con (*) son obligatorios. El RNT debe estar vigente.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <Alert variant="destructive" data-testid="onboarding-error-alert">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nombre">Nombre de la Propiedad *</Label>
                  <Input
                    id="nombre"
                    name="nombre"
                    placeholder="Hotel Paradise"
                    value={formData.nombre}
                    onChange={handleChange}
                    required
                    data-testid="onboarding-nombre-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="rnt">RNT (Registro Nacional de Turismo) *</Label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="rnt"
                      name="rnt"
                      placeholder="RNT-12345"
                      value={formData.rnt}
                      onChange={handleChange}
                      className="pl-10 font-mono"
                      required
                      data-testid="onboarding-rnt-input"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="direccion">Dirección *</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="direccion"
                    name="direccion"
                    placeholder="Calle 123 #45-67"
                    value={formData.direccion}
                    onChange={handleChange}
                    className="pl-10"
                    required
                    data-testid="onboarding-direccion-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ciudad">Ciudad *</Label>
                  <Input
                    id="ciudad"
                    name="ciudad"
                    placeholder="Bogotá"
                    value={formData.ciudad}
                    onChange={handleChange}
                    required
                    data-testid="onboarding-ciudad-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="departamento">Departamento *</Label>
                  <Input
                    id="departamento"
                    name="departamento"
                    placeholder="Cundinamarca"
                    value={formData.departamento}
                    onChange={handleChange}
                    required
                    data-testid="onboarding-departamento-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pais">País *</Label>
                  <Input
                    id="pais"
                    name="pais"
                    value={formData.pais}
                    onChange={handleChange}
                    required
                    disabled
                    data-testid="onboarding-pais-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="telefono">Teléfono *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="telefono"
                      name="telefono"
                      type="tel"
                      placeholder="+57 300 123 4567"
                      value={formData.telefono}
                      onChange={handleChange}
                      className="pl-10"
                      required
                      data-testid="onboarding-telefono-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="info@hotel.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="pl-10"
                      required
                      data-testid="onboarding-email-input"
                    />
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tipo">Tipo de Propiedad *</Label>
                  <Select 
                    value={formData.tipo} 
                    onValueChange={(value) => handleSelectChange('tipo', value)}
                  >
                    <SelectTrigger data-testid="onboarding-tipo-select">
                      <SelectValue placeholder="Selecciona el tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Hotel">Hotel</SelectItem>
                      <SelectItem value="Hostal">Hostal</SelectItem>
                      <SelectItem value="Apartamento">Apartamento</SelectItem>
                      <SelectItem value="Casa">Casa</SelectItem>
                      <SelectItem value="Finca">Finca</SelectItem>
                      <SelectItem value="Otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="num_habitaciones">Número de Habitaciones *</Label>
                  <Input
                    id="num_habitaciones"
                    name="num_habitaciones"
                    type="number"
                    min="1"
                    placeholder="10"
                    value={formData.num_habitaciones}
                    onChange={handleChange}
                    required
                    data-testid="onboarding-habitaciones-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="descripcion">Descripción (Opcional)</Label>
                <Textarea
                  id="descripcion"
                  name="descripcion"
                  placeholder="Breve descripción de tu propiedad..."
                  value={formData.descripcion}
                  onChange={handleChange}
                  rows={3}
                  data-testid="onboarding-descripcion-textarea"
                />
              </div>

              <div className="bg-accent/50 border border-accent-foreground/20 rounded-lg p-4">
                <p className="text-sm text-accent-foreground">
                  <strong>Nota:</strong> El Registro Nacional de Turismo (RNT) es obligatorio para operar 
                  legalmente en Colombia. Si aún no lo tienes, visita el sitio de Confecámaras.
                </p>
              </div>

              <div className="flex gap-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => navigate('/dashboard')}
                  className="flex-1"
                  data-testid="onboarding-skip-btn"
                >
                  Omitir por ahora
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1" 
                  disabled={loading}
                  data-testid="onboarding-submit-btn"
                >
                  {loading ? 'Registrando...' : 'Registrar Propiedad'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
